import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FaGithub, FaLinkedinIn, FaEnvelope, FaWhatsapp, FaPhoneAlt } from "react-icons/fa";
import { FiArrowUpRight } from "react-icons/fi";
import { gsap } from "../../lib/gsap";
import profile from "../../data/profile";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function ContactFooterSection() {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const contentRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [time, setTime] = useState("");

  useEffect(() => {
    const upd = () => setTime(new Date().toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit", second: "2-digit" }));
    upd();
    const id = setInterval(upd, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const s = sectionRef.current;
    const scroller = document.querySelector(".main-scroller");
    if (!s || !scroller) return;
    let active = false;
    function reset() { gsap.set(contentRef.current, { opacity: 0, y: 30 }); }
    function play() { gsap.to(contentRef.current, { opacity: 1, y: 0, duration: 0.9, ease: "power3.out" }); }
    reset();
    // Also exposed globally so App.js's goTo() can call this synchronously
    // inside the originating touchend/wheel/keydown handler. Some mobile
    // browsers (notably iOS Safari) reject video.play() once it's called
    // from an async scroll-listener callback a few ticks removed from the
    // actual user gesture, even for a muted video.
    window.__playContactVideo = () => videoRef.current?.play().catch(() => {});
    function onScroll() {
      const inRange = Math.abs(scroller.scrollTop - s.offsetTop) < window.innerHeight * 0.5;
      if (inRange && !active) { active = true; play(); videoRef.current?.play().catch(()=>{}); }
      if (!inRange && active) { active = false; reset(); }
    }
    scroller.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // catch the case where this section is already in view on mount
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      delete window.__playContactVideo;
    };
  }, []);

  async function submitEmail(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message");
      return;
    }
    setSending(true);
    try {
      // Backend emails Anish directly and stores the message, so no mail
      // client handoff is needed on the visitor's end.
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent. I'll reply within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error("Contact submission failed", err);
      // Fallback so the message isn't lost if the backend is unreachable.
      const subject = form.subject ? form.subject : `Portfolio enquiry from ${form.name}`;
      const body = `Hi Anish,\n\n${form.message}\n\nRegards,\n${form.name}\n${form.email}`;
      const mailto = `mailto:${profile.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailto;
      toast.error("Couldn't reach the server, opening your mail client instead.");
    } finally {
      setSending(false);
    }
  }

  const year = new Date().getFullYear();

  return (
    <section ref={sectionRef} className="snap-section" data-testid="contact-section" style={{ background: "var(--ink)", color: "var(--paper)" }}>
      {/* Background video */}
      <video ref={videoRef} src="/assets/intro.mp4" muted loop playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "grayscale(0.4) brightness(0.32) contrast(1.05)", zIndex: 0 }}
      />
      <div className="absolute inset-0 z-[1]" style={{
        background: "radial-gradient(60% 60% at 60% 45%, rgba(0,0,0,0.15) 0%, rgba(10,10,11,0.95) 90%)",
      }} />

      <div ref={contentRef} className="relative h-full w-full grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 px-6 md:px-16 pt-14 pb-14 md:py-16 z-[2]">
        {/* Left: identity */}
        <div className="md:col-span-6 flex flex-col justify-between">
          <div>
            <p className="hidden md:flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[color:rgba(245,242,236,0.6)]">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              {getGreeting()} · {time} GMT
            </p>
            <p className="md:mt-6 font-mono text-[9px] md:text-[10px] tracking-[0.35em] uppercase text-[var(--accent)]">Contact</p>
            <h2 className="mt-1 md:mt-2 font-serif text-[clamp(1.75rem,10vw,7rem)] leading-[0.9] md:leading-[0.85] tracking-tight">
              Let&apos;s build<br />something <span className="italic text-[var(--accent)]">trusted.</span>
            </h2>
            <p className="mt-2 md:mt-6 max-w-md text-[12px] md:text-sm leading-snug text-[color:rgba(245,242,236,0.7)]">
      Open to Business Analyst, Strategy Analyst and consulting opportunities across the UK. Full-time and internships.
            </p>
          </div>

          <div className="mt-3 md:mt-8 space-y-1 md:space-y-2">
            <a href={`mailto:${profile.email}`} data-testid="footer-email"
               className="inline-flex items-center gap-2 md:gap-3 text-sm md:text-xl font-serif hover:text-[var(--accent)] transition">
              <FaEnvelope size={14} /> {profile.email}
            </a>
            <a href={`tel:${profile.phoneRaw}`} data-testid="footer-phone"
               className="flex items-center gap-2 md:gap-3 text-[13px] md:text-base font-serif text-[color:rgba(245,242,236,0.85)] hover:text-[var(--accent)] transition">
              <FaPhoneAlt size={12} /> {profile.phone}
            </a>
            <div className="flex flex-wrap items-center gap-2.5 md:gap-4 pt-1 md:pt-2">
              <a href={profile.socials[0].href} target="_blank" rel="noopener noreferrer" data-testid="footer-github"
                 className="inline-flex items-center gap-1.5 md:gap-2 text-[12px] md:text-sm text-[color:rgba(245,242,236,0.7)] hover:text-[var(--paper)]">
                <FaGithub /> GitHub
              </a>
              <span className="text-[color:rgba(245,242,236,0.3)]">|</span>
              <a href={profile.socials[1].href} target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin"
                 className="inline-flex items-center gap-1.5 md:gap-2 text-[12px] md:text-sm text-[color:rgba(245,242,236,0.7)] hover:text-[var(--paper)]">
                <FaLinkedinIn /> LinkedIn
              </a>
              <span className="text-[color:rgba(245,242,236,0.3)]">|</span>
              <a href={`${profile.whatsappBase}?text=${encodeURIComponent("Hi Anish, I found your portfolio and would like to connect.")}`}
                 target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp"
                 className="inline-flex items-center gap-1.5 md:gap-2 text-[12px] md:text-sm text-[color:rgba(245,242,236,0.7)] hover:text-emerald-400">
                <FaWhatsapp /> WhatsApp
              </a>
              <span className="text-[color:rgba(245,242,236,0.3)]">|</span>
              <a href={profile.resumeUrl} target="_blank" rel="noopener noreferrer" download data-testid="footer-cv"
                 className="inline-flex items-center gap-1.5 md:gap-2 text-[12px] md:text-sm text-[color:rgba(245,242,236,0.7)] hover:text-[var(--paper)]">
                Download CV <FiArrowUpRight />
              </a>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="md:col-span-6 flex flex-col justify-center">
          <div className="rounded-2xl p-4 md:p-8 border border-[rgba(245,242,236,0.2)] bg-[rgba(10,10,11,0.85)] backdrop-blur-xl">
            <p className="font-mono text-[9px] md:text-[10px] tracking-[0.35em] uppercase text-[var(--accent)] mb-2 md:mb-5">Send a message</p>
            {sent ? (
              <div className="text-center py-6 md:py-10">
                <div className="font-serif text-2xl md:text-3xl italic text-[var(--accent)]">Sent.</div>
                <p className="text-[13px] md:text-sm text-[color:rgba(245,242,236,0.7)] mt-2 md:mt-3">I'll reply within 24 hours.</p>
                <button onClick={() => setSent(false)} data-testid="contact-reset"
                        className="mt-3 md:mt-6 text-[13px] md:text-sm underline underline-offset-4 hover:text-[var(--accent)]">Send another</button>
              </div>
            ) : (
              <form onSubmit={submitEmail} className="space-y-2 md:space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                  <input required data-testid="contact-name"
                    value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    onKeyDown={e => e.stopPropagation()}
                    placeholder="Your name"
                    className="w-full bg-transparent border-b border-[rgba(245,242,236,0.2)] py-1.5 md:py-3 text-[13px] md:text-sm focus:outline-none focus:border-[var(--accent)] transition placeholder:text-[color:rgba(245,242,236,0.4)]" />
                  <input required type="email" data-testid="contact-email"
                    value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    onKeyDown={e => e.stopPropagation()}
                    placeholder="Email address"
                    className="w-full bg-transparent border-b border-[rgba(245,242,236,0.2)] py-1.5 md:py-3 text-[13px] md:text-sm focus:outline-none focus:border-[var(--accent)] transition placeholder:text-[color:rgba(245,242,236,0.4)]" />
                </div>
                <input data-testid="contact-subject"
                  value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                  onKeyDown={e => e.stopPropagation()}
                  placeholder="Subject (optional)"
                  className="w-full bg-transparent border-b border-[rgba(245,242,236,0.2)] py-1.5 md:py-3 text-[13px] md:text-sm focus:outline-none focus:border-[var(--accent)] transition placeholder:text-[color:rgba(245,242,236,0.4)]" />
                <textarea required rows={3} data-testid="contact-message"
                  value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                  onKeyDown={e => e.stopPropagation()}
                  placeholder="Tell me about the role, project or idea…"
                  className="w-full bg-transparent border-b border-[rgba(245,242,236,0.2)] py-1.5 md:py-3 text-[13px] md:text-sm focus:outline-none focus:border-[var(--accent)] transition placeholder:text-[color:rgba(245,242,236,0.4)] resize-none md:rows-4" />
                <div className="flex flex-col sm:flex-row gap-1.5 md:gap-2">
                  <button type="submit" disabled={sending} data-testid="contact-submit"
                    className="inline-flex items-center justify-center gap-2 flex-1 rounded-full font-semibold text-[13px] md:text-sm px-4 py-2 md:px-5 md:py-3 bg-[var(--paper)] text-[var(--ink)] hover:bg-[var(--accent)] hover:text-[var(--accent-ink)] transition disabled:opacity-60 disabled:cursor-not-allowed">
                    {sending ? "Opening…" : <><FaEnvelope size={13} /> Send via Email</>}
                  </button>
                  <a
                    href={`${profile.whatsappBase}?text=${encodeURIComponent(
                      `Hi Anish,${form.name ? `\n\nMy name is ${form.name}.` : ""}${form.subject ? `\nSubject: ${form.subject}` : ""}${form.message ? `\n\n${form.message}` : "\n\nI came across your portfolio and would like to connect."}${form.email ? `\n\nReply to: ${form.email}` : ""}`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp"
                    className="inline-flex items-center justify-center gap-2 flex-1 rounded-full font-semibold text-[13px] md:text-sm px-4 py-2 md:px-5 md:py-3 bg-emerald-500 text-white hover:bg-emerald-600 transition"
                  >
                    <FaWhatsapp size={15} /> Send via WhatsApp
                  </a>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="absolute bottom-0 left-0 right-0 px-6 md:px-16 py-2 md:py-5 flex flex-col md:flex-row items-center justify-between gap-1 md:gap-2 border-t border-[rgba(245,242,236,0.1)] z-[3] bg-[var(--ink)] md:bg-transparent">
        <p className="font-mono text-[8px] md:text-[10px] tracking-[0.2em] md:tracking-[0.25em] uppercase text-[color:rgba(245,242,236,0.5)]">
          © {year} {profile.name.full} · All rights reserved
        </p>
        <p className="hidden md:block font-mono text-[10px] tracking-[0.25em] uppercase text-[color:rgba(245,242,236,0.5)]">
          Designed & Built with precision · <span className="text-[var(--accent)]">✦</span>
        </p>
      </div>
    </section>
  );
}
